import moment from 'moment'
import { useState } from 'react'
import {
  Alert,
  Button,
  Col,
  FloatingLabel,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { GraphQLError } from 'graphql'
import { useInsertCollectionSlotsMutation } from '../generated/urql.administrator'
import toastsService from '../lib/toastsService'

type FormCollectionSlotGenerator = {
  dayFrom: string
  dayTo: string
  timeFrom: string
  timeTo: string
  durationMinutes: string
}

type CollectionSlotGeneratorModalProps = {
  isVisible: boolean
  onClose: () => void
  collectionPointId: number
}

const CollectionSlotGeneratorModal = ({
  isVisible,
  onClose,
  collectionPointId,
}: CollectionSlotGeneratorModalProps) => {
  const {
    handleSubmit,
    formState: { errors },
    register,
    getValues,
  } = useForm<FormCollectionSlotGenerator>()
  const [{ fetching: isInserting }, insertCollectionSlots] =
    useInsertCollectionSlotsMutation()

  const onGenerateSlots = async (values: FormCollectionSlotGenerator) => {
    const generatedSlots = generateSlotsForRange(values)
    const objects = generatedSlots.map((s) => ({
      ...s,
      collectionPointId,
    }))
    const { error } = await insertCollectionSlots({ objects })

    if (error) {
      const code = error.graphQLErrors.find(
        (e: GraphQLError) => e.extensions?.code === 'constraint-violation'
      )
      const message = code
        ? 'Zablokowano aby zapobiec duplikatom terminów'
        : error.message || 'Coś poszło nie tak'
      toastsService.add({
        title: 'Błąd!',
        message,
        variant: 'danger',
      })
    } else {
      toastsService.add({
        title: 'Sukces!',
        message: 'Terminy zostały wygenerowanie poprawnie',
        variant: 'success',
      })
      onClose()
    }
  }

  return (
    <Modal show={isVisible} onHide={onClose} size="lg">
      <Form onSubmit={handleSubmit(onGenerateSlots)}>
        <Modal.Header closeButton>
          <Modal.Title>Generator terminów</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <FloatingLabel label="Dzień od">
                <Form.Control
                  isInvalid={!!errors.dayFrom}
                  type="date"
                  {...register('dayFrom', {
                    required: 'Proszę wybrać dzień',
                  })}
                />
                {errors.dayFrom && (
                  <Form.Control.Feedback>
                    {errors.dayFrom.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel label="Dzień do">
                <Form.Control
                  isInvalid={!!errors.dayTo}
                  type="date"
                  {...register('dayTo', {
                    required: 'Proszę wybrać dzień',
                    validate: (value) =>
                      value >= getValues('dayFrom') ||
                      'Data "do" nie może być wcześniejsza niż "od"',
                  })}
                />
                {errors.dayTo && (
                  <Form.Control.Feedback>
                    {errors.dayTo.message}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>
            </Col>
          </Row>

          <FloatingLabel label="Czas od" className="mb-3">
            <Form.Control
              isInvalid={!!errors.timeFrom}
              type="time"
              {...register('timeFrom', {
                required: 'Proszę wybrać godzinę',
              })}
            />
            {errors.timeFrom && (
              <Form.Control.Feedback>
                {errors.timeFrom.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Czas do" className="mb-3">
            <Form.Control
              isInvalid={!!errors.timeTo}
              type="time"
              {...register('timeTo', {
                required: 'Proszę wybrać godzinę',
              })}
            />
            {errors.timeTo && (
              <Form.Control.Feedback>
                {errors.timeTo.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Długość pojedynczego terminu" className="mb-3">
            <Form.Select
              {...register('durationMinutes', {
                required: 'Proszę opcję',
              })}
              defaultValue={10}
            >
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
            </Form.Select>
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            type="submit"
            disabled={isInserting}
            className="d-flex gap-2 align-items-center justify-content-center"
            style={{ width: 110 }}
          >
            {isInserting && (
              <Spinner size="sm" animation="border" role="status" />
            )}
            Generuj
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default CollectionSlotGeneratorModal

type Slot = { dateFrom: string; dateTo: string }

const generateSlotsForRange = (
  values: FormCollectionSlotGenerator
): Slot[] => {
  const { dayFrom, dayTo, timeFrom, timeTo, durationMinutes } = values

  const currentDay = moment(dayFrom, 'YYYY-MM-DD', true)
  const lastDay = moment(dayTo, 'YYYY-MM-DD', true)
  const out: Slot[] = []

  while (currentDay.isSameOrBefore(lastDay, 'day')) {
    const day = currentDay.format('YYYY-MM-DD')
    let start = moment(`${day} ${timeFrom}`, 'YYYY-MM-DD HH:mm', true)
    const endWindow = moment(`${day} ${timeTo}`, 'YYYY-MM-DD HH:mm', true)

    while (start.isBefore(endWindow)) {
      const next = start.clone().add(Number(durationMinutes), 'minutes')
      if (next.isAfter(endWindow)) break

      out.push({
        dateFrom: start.toDate().toISOString(),
        dateTo: next.toDate().toISOString(),
      })

      start = next
    }

    currentDay.add(1, 'day')
  }
  return out
}
